import { CalendarCheck, Cpu, Store, Zap } from 'lucide-react';

/**
 * DialogMe vision, mission, positioning and strategy — the /vision page and the About summary.
 * Mirrors docs/strategy/dialogme.md (public wording: Fnk is always "our own AI stack"; no account
 * gates or funding detail — those stay on the investor pages).
 */

export const vision = {
  statement: 'The language model is the new interface to a business: buttons, menus and forms give way to human-like conversation, in text or by voice.',
  support: 'For the salon, the clinic and the small online store — not only for the enterprise with an AI team.',
  body: [
    'For thirty years customers reached a business through buttons, menus and forms — built for a screen and learned one click at a time. Language models end that: the customer says what they need in their own words, typed or spoken.',
    'The business on the other side has to understand and act — answer, book, sell, take payment — whether the one asking is a person in a messaging app or an AI agent working on their behalf.',
  ],
};

export const mission = {
  statement: 'Give every small business the customer conversation only large companies could afford.',
  support: 'Answered in seconds, day or night — and finished. The booking on the calendar, the order placed, the payment taken.',
  outcomes: [
    ['Answered', 'From your own documents and catalog, in seconds, at any hour.'],
    ['Booked', 'Real availability, confirmed and written back to your calendar.'],
    ['Sold and paid', 'Products shown in the chat; payment taken behind your confirmation.'],
    ['Handed over properly', 'A structured ticket with the full context when a person is needed.'],
  ] as const,
};

export const positioning = {
  line: 'The Receptionist That Finishes the Job',
  statement:
    'A managed platform for businesses whose customers write to ask, book, order or pay. It answers from your content, books real availability and takes payment behind your confirmation — with the integrations already built, from €39 a month.',
  versus: [
    ['Chatbots you can afford', 'Answer questions, then hand off the moment money is involved.'],
    ['Agents that act', 'Priced per resolution, for enterprises, on models rented per token.'],
    ['DialogMe', 'Finishes the job, with the integrations built and inference we own — priced per conversation, not per seat.'],
  ] as const,
  is: [
    'A managed subscription — nothing to install',
    'An agent that completes the transaction',
    'Priced per conversation',
    'Reached in messaging apps, and by AI agents over MCP',
  ],
  isNot: [
    'A FAQ bot that hands off when money is involved',
    'A chatbot builder you configure from scratch',
    'Priced per seat or per resolution',
    'An integration project you have to run',
  ],
};

export const strategy = {
  lead: 'We grow by what keeps businesses paying, not by what brings the most signups. The order is the strategy.',
  choices: [
    {
      icon: CalendarCheck,
      title: 'Start where the result is countable',
      description:
        'Appointment businesses first — salons, clinics, studios, tutors — where a conversation ends in a booking on your own calendar. Online stores next, as checkout ships.',
    },
    {
      icon: Store,
      title: 'Small business first',
      description: 'One person decides, in days. We earn our references from owners who try something new — not from a procurement process.',
    },
    {
      icon: Cpu,
      title: 'Own the inference',
      description: 'DialogMe runs on its own AI stack, Fnk. No per-token bill underneath, so we price the conversation, not the seat.',
    },
    {
      icon: Zap,
      title: 'Self-serve is the product',
      description: 'From signup to a working assistant with no one on a call. A question asked twice is something to fix in the product.',
    },
  ],
  phases: [
    ['01', 'Design partners', 'Hand-onboarded businesses in the appointment verticals, on weekly loops — and onboarding you finish on your own in minutes.'],
    ['02', 'The vertical that stays', 'Concentrate on the industry that keeps using it, through the associations and chambers it already trusts.'],
    ['03', 'Partners', 'Agencies that build one-off bots today bring their clients onto a platform that keeps working.'],
  ] as const,
  longTerm:
    'A small business rents a stack of tools — a site builder, a booking page, a catalog, a helpdesk. Each is a database with an interface on top, and the interface is the part that is ceasing to matter.',
  longTermPunch: 'We are building the agent layer those tools are reached through.',
};
